<script setup lang="ts">
    import { useI18n } from 'vue-i18n';
    import { contactRoleKeys, phoneLabelKeys, type FinderContact } from '../../utils/postcodeFinder/data';

    interface Props {
        readonly contact: FinderContact;
    }
    defineProps<Props>();
    const { t } = useI18n();
    const telephoneLink = (number: string) => new URL(`tel:${number}`).href;
    const emailLink = (email: string) => new URL(`mailto:${encodeURIComponent(email)}`).href;
</script>

<template>
    <li
        class="finder-contact"
        :data-main-contact="contact.isMainContact"
    >
        <div class="finder-contact__heading">
            <h3>{{ contact.name }}</h3>
            <span
                v-if="contact.isMainContact"
                class="finder-contact__badge"
                >{{ t('postcodeFinder.mainContact') }}</span
            >
        </div>
        <p class="finder-contact__role">{{ t(contactRoleKeys[contact.role]) }}</p>
        <ul v-if="contact.phoneNumbers.length || contact.emails.length">
            <li
                v-for="(phone, index) in contact.phoneNumbers"
                :key="index"
            >
                <span class="finder-contact__label">{{ t(phoneLabelKeys[phone.label]) }}</span>
                <a :href="telephoneLink(phone.number)">{{ phone.number }}</a>
            </li>
            <li
                v-for="email in contact.emails"
                :key="email"
            >
                <span class="finder-contact__label">{{ t('postcodeFinder.email') }}</span>
                <a :href="emailLink(email)">{{ email }}</a>
            </li>
        </ul>
    </li>
</template>

<style scoped lang="scss">
    .finder-contact {
        min-width: 0;
        padding: var(--spacing);
        border-radius: var(--border-radius);
        background: var(--white);
        overflow-wrap: anywhere;
        &[data-main-contact='true'] {
            background: var(--finder-tint);
        }
        &__heading {
            display: flex;
            flex-wrap: wrap;
            align-items: start;
            gap: var(--spacing--05);
        }
        h3 {
            flex: 1 1 10rem;
            margin: 0;
            color: var(--black);
            font-family: var(--font-hero-heading);
            font-size: 1.125rem;
            font-weight: 700;
            line-height: 1.4;
            letter-spacing: normal;
        }
        &__badge {
            padding: var(--spacing--025) var(--spacing--05);
            border-radius: var(--border-radius-circle);
            background: color-mix(in srgb, var(--brand-primary) 12%, var(--white));
            color: var(--brand-primary-dark);
            font-size: 0.75rem;
            font-weight: 700;
            line-height: 1.5;
        }
        &__role {
            margin: var(--spacing--05) 0 0;
            color: var(--gray);
            font-size: 0.9rem;
            line-height: 1.5;
        }
        ul {
            display: grid;
            gap: var(--spacing);
            list-style: none;
            margin: var(--spacing--1-5) 0 0;
            padding: 0;
        }
        &__label {
            display: block;
            margin-bottom: var(--spacing--025);
            color: var(--gray);
            font-size: 0.8rem;
            line-height: 1.4;
        }
        a {
            display: inline-block;
            max-width: 100%;
            border-radius: var(--border-radius-xs);
            color: var(--brand-primary-dark);
            font-size: 1rem;
            font-weight: 700;
            line-height: 1.5;
            text-decoration: underline;
            text-underline-offset: 3px;
            &:hover {
                color: var(--brand-primary);
                text-decoration-thickness: 2px;
            }
            &:focus-visible {
                outline: 3px solid var(--color-focus);
                outline-offset: 3px;
            }
            &:active {
                color: var(--black);
                background: var(--finder-tint);
            }
        }
    }
</style>
